import type {
	ChatMessageRecord,
	ChatServiceBuildMetadata,
	ChatTurnCompletedPayload,
	WorkoutPreviewRecord,
} from '@entities/AIMessenger/api'

export type ChatMessageRole = 'user' | 'assistant' | 'system' | 'tool' | 'error' | 'separator'
export type ChatMessageStatus = 'sent' | 'sending' | 'pending' | 'failed'

export type CoachChatMessage = {
	id: string
	sessionId?: string
	turnId?: string | null
	sequenceNo?: number
	role: ChatMessageRole
	text: string
	createdAt: string
	status: ChatMessageStatus
	previewId?: string | null
	preview?: WorkoutPreviewRecord | null
	errorCode?: string | null
	requestId?: string | null
	requestPayload?: ChatTurnRequestPayload
	chatEndpoint?: string | null
	chatService?: ChatServiceBuildMetadata | null
}

export type CoachChatCache = {
	byId: Record<string, CoachChatMessage>
	orderedIds: string[]
}

export type PinnedChatContext = {
	exerciseId?: string
	exerciseName?: string
	workoutId?: string
	workoutTitle?: string
	workoutHistoryId?: string
	justFinishedWorkout?: boolean
	initialMessage?: string
}

export type ChatTurnRequestPayload = {
	message: string
	pinned_exercise_id?: string
	pinned_workout_id?: string
}

export const createEmptyChatCache = (): CoachChatCache => ({
	byId: {},
	orderedIds: [],
})

export const getAssistantText = (
	turn: ChatTurnCompletedPayload | Record<string, unknown>
): string => {
	const directText = stringValue(turn.assistant_text) || stringValue(turn.message)
	if (directText) return directText
	const decision = objectValue(turn.decision)
	return stringValue(decision?.assistant_text) || stringValue(decision?.message) || ''
}

export const getDecisionKind = (
	turn: ChatTurnCompletedPayload | Record<string, unknown>
): string => {
	const decision = objectValue(turn.decision)
	return stringValue(turn.kind) || stringValue(decision?.kind)
}

export const getPreviewId = (
	turn: ChatTurnCompletedPayload | Record<string, unknown>
): string | null => {
	const decision = objectValue(turn.decision)
	return stringValue(turn.preview_id) || stringValue(decision?.preview_id) || null
}

export const getRequiresApproval = (
	turn: ChatTurnCompletedPayload | Record<string, unknown>
): boolean => {
	const decision = objectValue(turn.decision)
	return Boolean(turn.requires_approval ?? decision?.requires_approval ?? false)
}

export const getRuntimeStatus = (
	turn: ChatTurnCompletedPayload | Record<string, unknown>
): string => {
	const decision = objectValue(turn.decision)
	return stringValue(turn.runtime_status) || stringValue(decision?.runtime_status) || 'ok'
}

export const buildTurnPayload = (
	message: string,
	pinnedContext: PinnedChatContext | null
): ChatTurnRequestPayload => {
	const payload: ChatTurnRequestPayload = { message }
	if (pinnedContext?.exerciseId) {
		payload.pinned_exercise_id = pinnedContext.exerciseId
	}
	if (pinnedContext?.workoutId) {
		payload.pinned_workout_id = pinnedContext.workoutId
	}
	return payload
}

export const messageFromRecord = (record: ChatMessageRecord): CoachChatMessage | null => {
	const role = normalizeRole(record.role)
	if (role === 'tool' || role === 'system') {
		return null
	}
	const decision = objectValue(record.content_json?.decision)
	const text =
		record.content_text ||
		stringValue(decision?.assistant_text) ||
		stringValue(record.content_json?.message) ||
		''
	if (!text.trim() && !record.content_json?.preview_id) {
		return null
	}
	return {
		id: record.id,
		sessionId: record.session_id,
		turnId: record.turn_id,
		sequenceNo: record.sequence_no,
		role,
		text,
		createdAt: record.created_at,
		status: 'sent',
		previewId:
			stringValue(record.content_json?.preview_id) ||
			stringValue(decision?.preview_id) ||
			null,
	}
}

export const assistantMessageFromTurn = (
	turn: ChatTurnCompletedPayload,
	preview?: WorkoutPreviewRecord | null
): CoachChatMessage => ({
	id: turn.message_id,
	sessionId: turn.session_id,
	turnId: turn.turn_id,
	role: getRuntimeStatus(turn) === 'provider_unavailable' ? 'error' : 'assistant',
	text: getAssistantText(turn),
	createdAt: turn.created_at,
	status: getRuntimeStatus(turn) === 'provider_unavailable' ? 'failed' : 'sent',
	previewId: getPreviewId(turn),
	preview: preview ?? turn.previewRecord ?? null,
	errorCode: getRuntimeStatus(turn) === 'provider_unavailable' ? 'provider_unavailable' : null,
	requestId: turn.transport?.requestId ?? null,
	chatEndpoint: turn.transport?.chatEndpoint ?? null,
	chatService: turn.transport?.service ?? null,
})

export const mergeChatMessages = (
	cache: CoachChatCache,
	messages: CoachChatMessage[],
	mode: 'append' | 'prepend' | 'replace' = 'append'
): CoachChatCache => {
	const byId = mode === 'replace' ? {} : { ...cache.byId }
	const existingIds = mode === 'replace' ? [] : cache.orderedIds.filter(id => byId[id])
	const nextIds = [...existingIds]

	for (const message of messages) {
		byId[message.id] = { ...byId[message.id], ...message }
		if (!nextIds.includes(message.id)) {
			if (mode === 'prepend') {
				nextIds.unshift(message.id)
			} else {
				nextIds.push(message.id)
			}
		}
	}

	nextIds.sort((leftId, rightId) => compareMessages(byId[leftId], byId[rightId]))

	return { byId, orderedIds: nextIds }
}

export const replaceChatMessage = (
	cache: CoachChatCache,
	id: string,
	replacement: CoachChatMessage
): CoachChatCache => {
	const byId = { ...cache.byId }
	const orderedIds = cache.orderedIds.map(itemId => (itemId === id ? replacement.id : itemId))
	delete byId[id]
	byId[replacement.id] = replacement
	return {
		byId,
		orderedIds: Array.from(new Set(orderedIds)).sort((leftId, rightId) =>
			compareMessages(byId[leftId], byId[rightId])
		),
	}
}

export const updatePreviewInCache = (
	cache: CoachChatCache,
	previewId: string,
	update: Partial<WorkoutPreviewRecord>
): CoachChatCache => {
	const byId = { ...cache.byId }
	for (const id of cache.orderedIds) {
		const message = byId[id]
		if (message?.preview?.id === previewId) {
			byId[id] = { ...message, preview: { ...message.preview, ...update } }
		}
	}
	return { ...cache, byId }
}

export const messagesAfterLatestSeparator = (messages: CoachChatMessage[]): CoachChatMessage[] => {
	let latestSeparatorIndex = -1
	for (let index = messages.length - 1; index >= 0; index -= 1) {
		if (messages[index].role === 'separator') {
			latestSeparatorIndex = index
			break
		}
	}
	return latestSeparatorIndex >= 0 ? messages.slice(latestSeparatorIndex + 1) : messages
}

export const buildPromptChips = ({
	pinnedContext,
	pendingPreview,
	messages,
}: {
	pinnedContext: PinnedChatContext | null
	pendingPreview: WorkoutPreviewRecord | null
	messages: CoachChatMessage[]
}): string[] => {
	if (pendingPreview) {
		return ['Короче', 'Без прыжков', 'Больше кора']
	}
	if (pinnedContext?.exerciseId) {
		return rotateChipSet(
			[
				['Как выполнять правильно?', 'Каких ошибок избегать?', 'Сделай проще'],
				['Что чувствовать в мышцах?', 'Как прогрессировать?', 'Чем заменить?'],
			],
			messages.length
		)
	}
	if (pinnedContext?.workoutId) {
		return rotateChipSet(
			[
				['Сделай короче', 'Сделай сложнее', 'Замени одно упражнение'],
				['Уменьши нагрузку', 'Добавь кор', 'Подстрой под дом'],
			],
			messages.length
		)
	}
	if (pinnedContext?.workoutHistoryId || pinnedContext?.justFinishedWorkout) {
		return ['Как оценить нагрузку?', 'Восстановиться или тренироваться?', 'Было слишком легко?']
	}

	const activeMessages = messagesAfterLatestSeparator(messages).filter(
		message => message.role !== 'separator'
	)
	const latestMessage = [...activeMessages]
		.reverse()
		.find(message => message.role !== 'system' && message.role !== 'tool')
	const latestAssistantText =
		[...activeMessages]
			.reverse()
			.find(message => message.role === 'assistant')
			?.text.toLowerCase() ?? ''
	const latestUserText =
		[...activeMessages]
			.reverse()
			.find(message => message.role === 'user')
			?.text.toLowerCase() ?? ''

	if (latestMessage?.role === 'error') {
		return ['Сформулировать короче', 'Попробовать позже', 'Упростить запрос']
	}
	const assistantChoiceChips = chipsFromAssistantQuestion(latestAssistantText)
	if (assistantChoiceChips.length) {
		return assistantChoiceChips
	}
	if (/выбери|что хочешь|направлен/.test(latestAssistantText)) {
		return ['Силовая', 'Мобильность', 'Без прыжков']
	}
	if (/начинаем|создад|создава|превью|карточк/.test(latestAssistantText)) {
		return ['Покажи превью', 'Без прыжков', '15 минут']
	}
	if (/трениров|заняти|план|комплекс|созда|собер/.test(latestUserText)) {
		return ['Покажи превью', 'Легче', 'Только дома']
	}
	if (/болит|боль|дискомфорт|спин|колен|ше/.test(latestUserText)) {
		return ['Стало лучше', 'Так же болит', 'Стало хуже']
	}
	if (activeMessages.length === 0) {
		return ['Тренировка сегодня', 'Разминка', 'Техника']
	}

	return rotateChipSet(
		[
			['Как размяться?', 'Подстроить нагрузку?', 'Разобрать упражнение'],
			['Что сделать сегодня?', 'Сделай легче', 'Сделай сложнее'],
			['Проверить технику', 'Нужна короткая тренировка', 'Как восстановиться?'],
		],
		activeMessages.length
	)
}

export const parsePinnedContext = (search: string): PinnedChatContext | null => {
	const params = new URLSearchParams(search)
	const context: PinnedChatContext = {
		exerciseId: params.get('pinned_exercise_id') || params.get('exercise_id') || undefined,
		exerciseName:
			params.get('pinned_exercise_name') || params.get('exercise_name') || undefined,
		workoutId: params.get('pinned_workout_id') || params.get('workout_id') || undefined,
		workoutTitle:
			params.get('pinned_workout_title') || params.get('workout_title') || undefined,
		workoutHistoryId: params.get('workout_history_id') || undefined,
		justFinishedWorkout: params.get('just_finished_workout') === 'true',
		initialMessage: params.get('initial_message') || undefined,
	}
	return Object.values(context).some(Boolean) ? context : null
}

export const pinnedContextLabel = (context: PinnedChatContext): string => {
	if (context.exerciseId) return `Упражнение: ${context.exerciseName || `#${context.exerciseId}`}`
	if (context.workoutId) return `Тренировка: ${context.workoutTitle || `#${context.workoutId}`}`
	if (context.workoutHistoryId || context.justFinishedWorkout) return 'Завершенная тренировка'
	return ''
}

const compareMessages = (left?: CoachChatMessage, right?: CoachChatMessage): number => {
	if (!left || !right) return 0
	if (typeof left.sequenceNo === 'number' && typeof right.sequenceNo === 'number') {
		return left.sequenceNo - right.sequenceNo
	}
	return Date.parse(left.createdAt) - Date.parse(right.createdAt)
}

const normalizeRole = (role: string): ChatMessageRole => {
	if (role === 'assistant') return 'assistant'
	if (role === 'system') return 'system'
	if (role === 'tool') return 'tool'
	if (role === 'error') return 'error'
	return 'user'
}

const objectValue = (value: unknown): Record<string, unknown> | null =>
	value && typeof value === 'object' && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null

const stringValue = (value: unknown): string =>
	typeof value === 'string' && value.trim() ? value : ''

const rotateChipSet = (sets: string[][], seed: number): string[] => sets[seed % sets.length]

const chipsFromAssistantQuestion = (text: string): string[] => {
	if (!text) return []
	if (/стало лучше.*так же.*хуже/.test(text)) {
		return ['Стало лучше', 'Так же', 'Стало хуже']
	}
	if (/нормально.*слишком легко.*тяжело/.test(text)) {
		return ['Нормально', 'Слишком легко', 'Тяжело']
	}
	if (/ноги?.*верх.*(кор|спин)/.test(text)) {
		return ['Ноги', 'Верх тела', 'Кор/спина']
	}
	if (/(все|всё) тело.*верх.*низ/.test(text)) {
		return ['Всё тело', 'Верх', 'Низ']
	}
	if (/коврик.*(тверд|твёрд)/.test(text)) {
		return ['Есть коврик', 'Твёрдая поверхность']
	}
	if (/дом.*зал|зал.*дом/.test(text)) {
		return ['Дома', 'В зале']
	}
	return []
}
